import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Menu, MenuItem, useTheme } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import CircleIcon from '@mui/icons-material/Circle';

const Editor = ({ noteID }) => {

    const [noteTitle, setNoteTitle] = useState('');
    const [blocks, setBlocks] = useState([]);
    const [selectedBlockId, setSelectedBlockId] = useState(null);
    const textAreaRefs = useRef([]);
    const [screenLoading, setScreenLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [isLocked, setIsLocked] = useState(false);
    

    useEffect(() => {
        const apiUrl = process.env.REACT_APP_BACKEND_API;
        const fetchBlocks = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${apiUrl}/users/get_notes/${noteID}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });
                const data = await response.json();
                setNoteTitle(data['title']);
                setIsLocked(data['is_locked']);
                const content = data['content'];
                if (content.length === 0) {
                    content.push({ id: `${Date.now()}`, text: '', position: 0, depth: 0 });
                }
                setBlocks(content);
            } catch (error) {
                console.error('Error fetching blocks:', error);
                window.location.href = "/dashboard";
            }
            setScreenLoading(false);
        };

        fetchBlocks();
    }, [noteID]);

    useEffect(() => {
        textAreaRefs.current = textAreaRefs.current.slice(0, blocks.length);
    }, [blocks]);

    const handleTitleChange = async (e) => {
        const newTitle = e.target.value;
        setNoteTitle(newTitle);

        const apiUrl = process.env.REACT_APP_BACKEND_API;
        const token = localStorage.getItem('token');
        try {
            await fetch(`${apiUrl}/users/edit_notes_title/${noteID}/${newTitle}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
        } catch (error) {
            console.error('Error updating title:', error);
        }
    };

    useEffect(() => {
        const apiUrl = process.env.REACT_APP_BACKEND_API;
        const sendBlocks = async () => {
            const token = localStorage.getItem('token');
            try {
            await fetch(`${apiUrl}/users/send_notes/${noteID}`, {
                method: 'POST',
                headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(blocks),
            });
            } catch (error) {
            console.error('Error sending blocks:', error);
            }
        };

        if (blocks.length > 0) {
            sendBlocks();
        }
    }, [blocks, noteID]);

    const handleChange = (e, id) => {
        const newBlocks = blocks.map(block => {
            if (block.id === id) {
                return { ...block, text: e.target.value };
            }
            return block;
        });
        setBlocks(newBlocks);
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
    
        const newBlocks = Array.from(blocks);
        const [movedBlock] = newBlocks.splice(result.source.index, 1);
        newBlocks.splice(result.destination.index, 0, movedBlock);
    
        // Update positions
        newBlocks.forEach((block, index) => {
            block.position = index;
        });

        setBlocks(newBlocks);
    };

    const handleClick = (event, blockID) => {
        if (isLocked) {
            return;
        }
        setAnchorEl(event.currentTarget);
        setSelectedBlockId(blockID);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedBlockId(null);
    };

    const handleKeyDown = (e, id) => {
        const index = blocks.findIndex(block => block.id === id);
        if (e.key === 'Enter') {
            e.preventDefault();
            const newBlocks = [...blocks];
            const newBlock = { id: `${Date.now()}`, text: '', position: index + 1, depth: blocks[index].depth };
            newBlocks.splice(index + 1, 0, newBlock);
            // Update positions of subsequent blocks
            for (let i = index + 2; i < newBlocks.length; i++) {
                newBlocks[i].position += 1;
            }
            setBlocks(newBlocks);
            setTimeout(() => {
                textAreaRefs.current[index + 1].focus();
            }, 0);
        } else if (e.key === 'Tab') {
            e.preventDefault();
            const newBlocks = blocks.map(block => {
                if (block.id === id) {
                    if (e.shiftKey) {
                        return { ...block, depth: Math.max(block.depth - 1, 0) }; // Min depth of 0
                    } else {
                        return { ...block, depth: Math.min(block.depth + 1, 2) }; // Max depth of 2
                    }
                }
                return block;
            });
            setBlocks(newBlocks);
        } else if (e.key === 'Backspace') {
            if (blocks[index].text === '' || (e.target.selectionStart === 0 && e.target.selectionEnd === 0)) {
                e.preventDefault();
                if (index > 0) {
                    const newBlocks = [...blocks];
                    const previousBlock = newBlocks[index - 1];
                    const currentBlock = newBlocks[index];
                    previousBlock.text += currentBlock.text;
                    newBlocks.splice(index, 1);
                    for (let i = index; i < newBlocks.length; i++) {
                        newBlocks[i].position -= 1;
                    }
                    setBlocks(newBlocks);
                    setTimeout(() => {
                        textAreaRefs.current[index - 1].focus();
                        textAreaRefs.current[index - 1].selectionStart = previousBlock.text.length;
                        textAreaRefs.current[index - 1].selectionEnd = previousBlock.text.length;
                    }, 0);
                }
            }
        } else if (e.key === 'ArrowUp') {
            if (index > 0 && e.target.selectionStart === 0 && e.target.selectionEnd === 0) {
                e.preventDefault();
                setTimeout(() => {
                    textAreaRefs.current[index - 1].focus();
                }, 0);
            }
        } else if (e.key === 'ArrowDown') {
            if (index < blocks.length - 1 && e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length) {
                e.preventDefault();
                setTimeout(() => {
                    textAreaRefs.current[index + 1].focus();
                }, 0);
            }
        }
    };

    const theme = useTheme();

    return (
        <Box>
            {screenLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                    <CircularProgress sx={{ color: 'primary.main' }} />
                </Box>
            ) : (
                <Box>
                    <Box
                        component="textarea"
                        type="text"
                        value={noteTitle}
                        onChange={handleTitleChange}
                        sx={{
                            fontSize: theme.typography.h3.fontSize,
                            padding: 0,
                            border: 'none',
                            outline: 'none',
                            width: '100%',
                            fontWeight: theme.typography.h3.fontWeight,
                            backgroundColor: 'transparent',
                            fontFamily: theme.typography.fontFamily,
                            marginBottom: theme.spacing(2),
                            overflow: 'hidden',
                            resize: 'none',
                            color: 'text.primary',
                            lineHeight: theme.typography.h3.lineHeight,
                        }}
                        rows={Math.min(3, Math.max(1, Math.ceil(noteTitle.length / (window.innerWidth / 20))))}
                        onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                        }}
                        disabled={isLocked}
                    />
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="blocks">
                            {(provided) => (
                                <Box
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    sx={{
                                        maxWidth: '800px',
                                    }}
                                >
                                    <Box className="block-editor">
                                        {Array.isArray(blocks) && blocks.map((block, index) => (
                                            block.id && <Draggable key={block.id} draggableId={block.id.toString()} index={index} isDragDisabled={isLocked}>
                                                {(provided) => (
                                                    <Box
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className="block"
                                                    >
                                                        <Box sx={{ marginLeft: `${block.depth * 40}px` }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                    <CircleIcon
                                                                        onClick={(event) => handleClick(event, block.id)}
                                                                        sx={{
                                                                            color: 'primary.main',
                                                                            fontSize: '16px',
                                                                            verticalAlign: 'top',
                                                                            marginTop: theme.spacing(1), // 8px
                                                                            padding: theme.spacing(0.5), // 4px
                                                                            cursor: !isLocked ? 'pointer' : 'default',
                                                                            borderRadius: '50%',
                                                                            '&:hover': {
                                                                                backgroundColor: !isLocked ? 'action.hover' : 'transparent',
                                                                                transform: 'scale(1.2)',
                                                                            },
                                                                        }}
                                                                    />
                                                                    <Menu
                                                                        anchorEl={anchorEl}
                                                                        open={open && selectedBlockId === block.id}
                                                                        onClose={handleClose}
                                                                        MenuListProps={{
                                                                            'aria-labelledby': 'circle-button',
                                                                        }}
                                                                        PaperProps={{
                                                                            sx: {
                                                                                minWidth: '140px',
                                                                                borderRadius: '6px',
                                                                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                                                                border: '1px solid',
                                                                                borderColor: 'divider',
                                                                                marginTop: theme.spacing(0.5),
                                                                            }
                                                                        }}
                                                                    >
                                                                        <MenuItem
                                                                            onClick={() => {
                                                                                const newBlocks = blocks.map(b => {
                                                                                    if (b.id === block.id) {
                                                                                        const { completed, ...rest } = b;
                                                                                        return completed ? rest : { ...b, completed: true };
                                                                                    }
                                                                                    return b;
                                                                                });
                                                                                setBlocks(newBlocks);
                                                                                handleClose();
                                                                            }}
                                                                            sx={{
                                                                                fontSize: '14px',
                                                                                padding: theme.spacing(0.5, 1.25),
                                                                                minHeight: 'auto',
                                                                                '&:hover': {
                                                                                    backgroundColor: 'action.hover',
                                                                                },
                                                                            }}
                                                                        >
                                                                            {block.completed ? '✓ Incomplete' : '✓ Complete'}
                                                                        </MenuItem>
                                                                    </Menu>
                                                                </Box>
                                                                <Box
                                                                    component="textarea"
                                                                    sx={{
                                                                        flex: 1,
                                                                        width: '100%',
                                                                        padding: theme.spacing(0.625), // 5px
                                                                        fontSize: theme.typography.body1.fontSize,
                                                                        overflow: 'hidden',
                                                                        resize: 'none',
                                                                        height: 'auto',
                                                                        border: 'none',
                                                                        outline: 'none',
                                                                        transition: '0.5s ease',
                                                                        verticalAlign: 'bottom',
                                                                        borderRadius: '2px',
                                                                        fontFamily: theme.typography.fontFamily,
                                                                        lineHeight: theme.typography.subtitle1.lineHeight,
                                                                        fontWeight: theme.typography.subtitle1.fontWeight,
                                                                        textDecoration: block.completed ? 'line-through' : 'none',
                                                                        backgroundColor: isLocked ? 'transparent' : 'inherit',
                                                                        color: block.completed ? 'text.disabled' : 'text.primary',
                                                                    }}
                                                                    ref={el => {
                                                                        textAreaRefs.current[index] = el;
                                                                        if (el) {
                                                                            el.style.height = 'auto';
                                                                            el.style.height = `${el.scrollHeight - 8}px`;
                                                                        }
                                                                    }}
                                                                    value={block.text}
                                                                    onChange={(e) => handleChange(e, block.id)}
                                                                    onKeyDown={(e) => handleKeyDown(e, block.id)}
                                                                    placeholder={blocks.length === 1 ? "Type here..." : ""}
                                                                    onInput={(e) => {
                                                                        e.target.style.height = 'auto';
                                                                        e.target.style.height = `${e.target.scrollHeight}px`;
                                                                    }}
                                                                    rows={1}
                                                                    disabled={isLocked}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </Box>
                                </Box>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Box>
            )}
        </Box>
    );
};

export default Editor;

